import React, { useRef } from "react";

import { EditProductModel } from "@admin/domain/products/models/edit/EditProductModel";
import { updateProduct } from "@admin/domain/products/api";
import convertToLanguageMap from "@admin/domain/languages/helpers";
import Editor from "@admin/components/Editor";
import DocumentsInput from "@admin/components/DocumentsInput";
import ExpertOpinion from "./ProductExpertOpinion";
import ProductVideos from "./ProductVideos";
import CrossSale from "./CrossSale";
import ProductAttributes from "./ProductAttributes";
import ProductAdditionalAttributes from "./ProductAdditionalAttributes";
import ProductPrices from "./ProductPrices";
import ProductSpecifications from "./ProductSpecifications";
import PageSeoParamsEditor from "@admin/components/PageSeoParamsEditor";
import SortableContext from "@admin/components/Sortable/SortableContext";
import ProductQuestionBlocks, {
    type ProductQuestionBlocksHandle,
} from "./ProductQuestionBlocks";

function prepareProduct(product: EditProductModel): EditProductModel {
    product.product_additional_attributes.variations =
        product.product_additional_attributes.variations.map((value) => {
            value.textsMap = convertToLanguageMap(value.texts);
            return value;
        });

    product.specifications.ingredients = product.specifications.ingredients.map(
        (value) => {
            value.textsMap = convertToLanguageMap(value.texts);
            return value;
        },
    );

    product.specifications.active_components =
        product.specifications.active_components.map((value) => {
            value.active_component.textsMap = convertToLanguageMap(
                value.active_component.texts,
            );
            return value;
        });

    if (product.expert_opinion) {
        product.expert_opinion.textsMap = convertToLanguageMap(
            product.expert_opinion.texts,
        );
    }

    return product;
}

export default function ProductEditor() {
    const product = prepareProduct(window.CMS.product as EditProductModel);
    const questionBlocksRef = useRef<ProductQuestionBlocksHandle | null>(null);

    const execute = async (body: FormData) => {
        await updateProduct(product.product_additional_attributes.id, body);
    };

    return (
        <SortableContext>
            <Editor
                backUrl={`/admin/products`}
                onSubmit={execute}
                onReset={() => questionBlocksRef.current?.reset()}
                prepareBody={(body) =>
                    questionBlocksRef.current?.appendToFormData(body)
                }
            >
                <ProductAttributes
                    productAttributes={product.product_attributes}
                />

                <ProductAdditionalAttributes
                    productAdditionalAttributes={
                        product.product_additional_attributes
                    }
                />

                <ProductPrices productPrices={product.prices} />

                <ProductSpecifications
                    specifications={product.specifications}
                />

                <ProductVideos defaultValue={product.product_videos} />

                <DocumentsInput
                    label="Документы"
                    name="documents[]"
                    accept=".xlsx, .xls, .doc, .docx, .ppt, .pptx, .txt, .pdf, .odt"
                    defaultValue={product.documents}
                />

                <DocumentsInput
                    label="Промо-материалы"
                    name="promo_materials[]"
                    accept=".xlsx, .xls, .doc, .docx, .ppt, .pptx, .txt, .pdf, .odt, .jpg, .jpeg, .png, .bmp"
                    defaultValue={product.promo_materials}
                />

                <DocumentsInput
                    label="Материалы для письма при покупке"
                    name="mail_materials[]"
                    accept=".xlsx, .xls, .doc, .docx, .ppt, .pptx, .txt, .pdf, .odt, .jpg, .jpeg, .png, .bmp"
                    defaultValue={product.mail_materials}
                />

                <ExpertOpinion productExpertOpinion={product.expert_opinion} />

                <CrossSale crossSales={product.cross_sale_products} />

                <ProductQuestionBlocks
                    ref={questionBlocksRef}
                    defaultValue={product.product_question_blocks || []}
                />

                <PageSeoParamsEditor includePrice />
            </Editor>
        </SortableContext>
    );
}
